import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ChevronRight, Sparkles, Gift } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Prefooter, Footer } from '@/components/layout'
import { getProductBySlug } from '@/data/products'

export function WelcomeBoxPage() {
  const { t } = useTranslation('common')

  const included = useMemo(
    () =>
      ['shampoo', 'conditioner', 'oil']
        .map((slug) => getProductBySlug(slug))
        .filter(Boolean),
    []
  )

  return (
    <main className="bg-cream">
      {/* Hero */}
      <section className="relative pt-20 md:pt-24 pb-20 md:pb-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-12 select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(14rem, 24vw, 26rem)', lineHeight: 0.85 }}
        >
          n.
        </div>

        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            {/* Image stage */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-7 relative"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <div className="absolute top-6 left-6 z-10 flex items-center gap-3">
                  <span className="nashi-numeral text-bruno-muted">01.</span>
                  <span className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {t('welcomeBox.heroBadge')}
                  </span>
                </div>
                <div className="absolute bottom-6 right-6 z-10 text-right">
                  <p className="font-display italic text-argan-deep text-3xl">n.</p>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted mt-1">
                    {t('welcomeBox.heroTag')}
                  </p>
                </div>

                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_55%,rgba(207,169,128,0.18),transparent_65%)]"
                />

                <motion.img
                  src="/images/welcome-box.jpg"
                  alt={t('welcomeBox.heroImageAlt')}
                  loading="eager"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.4, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </motion.div>

            {/* Info column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5 lg:pt-10"
            >
              <span className="nashi-eyebrow">{t('welcomeBox.heroEyebrow')}</span>

              <h1
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2.5rem, 5vw, 4.25rem)',
                  lineHeight: 0.96,
                  letterSpacing: '-0.02em',
                }}
              >
                {t('welcomeBox.heroTitle')}
              </h1>

              <p className="mt-4 font-display italic text-argan-deep text-xl md:text-2xl">
                {t('welcomeBox.heroSubtitle')}
              </p>

              <p className="mt-8 text-bruno-soft text-base md:text-lg leading-relaxed">
                {t('welcomeBox.heroBody')}
              </p>

              {/* Meta block */}
              <dl className="mt-10 grid grid-cols-2 gap-y-4 gap-x-6 pt-6 border-t border-bruno/10 text-[10px] uppercase tracking-[0.28em]">
                <div>
                  <dt className="text-bruno-muted">{t('welcomeBox.metaFormat')}</dt>
                  <dd className="mt-1 text-bruno tabular-nums">{t('welcomeBox.metaFormatValue')}</dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('welcomeBox.metaLine')}</dt>
                  <dd className="mt-1 text-bruno">{t('welcomeBox.metaLineValue')}</dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('welcomeBox.metaCategory')}</dt>
                  <dd className="mt-1 text-bruno">{t('welcomeBox.metaCategoryValue')}</dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('welcomeBox.metaAvailability')}</dt>
                  <dd className="mt-1 text-bruno">{t('welcomeBox.metaAvailabilityValue')}</dd>
                </div>
              </dl>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <LocalizedLink
                  to="storeLocator"
                  className="nashi-cta inline-flex items-center justify-center gap-2 px-7 py-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] hover:bg-argan-deep transition-colors duration-500"
                >
                  {t('welcomeBox.ctaPrimary')}
                  <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                </LocalizedLink>
                <LocalizedLink
                  to="contacts"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-bruno/30 text-bruno text-[11px] uppercase tracking-[0.2em] hover:border-argan hover:text-argan-deep transition-colors duration-500"
                >
                  {t('welcomeBox.ctaSecondary')}
                </LocalizedLink>
              </div>

              <p className="mt-6 flex items-start gap-2 text-xs italic text-bruno-muted">
                <Sparkles className="h-3.5 w-3.5 mt-0.5 text-argan" strokeWidth={1.5} />
                {t('welcomeBox.heroNote')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cosa contiene il cofanetto */}
      <section className="relative bg-cream-soft py-24 md:py-32 overflow-hidden border-y border-bruno/10">
        <div className="nashi-container">
          <div className="text-center mb-16 md:mb-20">
            <span className="nashi-eyebrow">{t('welcomeBox.boxEyebrow')}</span>
            <h2
              className="mt-5 font-display text-bruno"
              style={{
                fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
            >
              <span className="block">{t('welcomeBox.boxTitle1')}</span>
              <span className="italic font-light text-argan-deep">{t('welcomeBox.boxTitle2')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {included.map((ip, idx) => (
              <motion.div
                key={ip!.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: idx * 0.15 }}
                className="group text-center"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-cream mb-6">
                  <img
                    src={ip!.hoverImage || ip!.image}
                    alt={ip!.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <h3 className="font-display text-bruno text-xl md:text-2xl">{ip!.name}</h3>
                {ip!.detail?.subtitle && (
                  <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-bruno-muted">
                    {ip!.detail.subtitle}
                  </p>
                )}
                {ip!.detail?.description && (
                  <p className="mt-4 text-bruno-soft text-sm leading-relaxed max-w-xs mx-auto line-clamp-4">
                    {ip!.detail.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Come usarlo */}
      <section className="relative bg-cream py-24 md:py-32 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-14">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1 }}
            >
              <span className="nashi-eyebrow">{t('welcomeBox.idealEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.01em',
                }}
              >
                <span className="block">{t('welcomeBox.idealTitle1')}</span>
                <span className="block italic font-light text-argan-deep">{t('welcomeBox.idealTitle2')}</span>
              </h2>
              <p className="mt-6 text-bruno-soft text-base md:text-lg leading-relaxed">
                {t('welcomeBox.idealBody')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, delay: 0.15 }}
            >
              <span className="nashi-eyebrow">{t('welcomeBox.usageEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.01em',
                }}
              >
                <span className="block">{t('welcomeBox.usageTitle1')}</span>
                <span className="block italic font-light text-argan-deep">{t('welcomeBox.usageTitle2')}</span>
              </h2>
              <p className="mt-6 text-bruno-soft text-base md:text-lg leading-relaxed">
                {t('welcomeBox.usageBody')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Gift / Condizioni */}
      <section className="relative bg-cream-soft py-24 md:py-32 overflow-hidden">
        <div className="nashi-container">
          <div className="max-w-4xl mx-auto text-center">
            <Gift
              aria-hidden="true"
              className="mx-auto h-10 w-10 text-argan-deep mb-8"
              strokeWidth={1.2}
            />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="font-display text-bruno"
              style={{
                fontSize: 'clamp(1.75rem, 3.2vw, 2.75rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {t('welcomeBox.giftTitle1')}{' '}
              <span className="italic font-light text-argan-deep">{t('welcomeBox.giftTitle2')}</span>
            </motion.h2>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <LocalizedLink
                to="storeLocator"
                className="nashi-cta inline-flex items-center justify-center gap-2 px-7 py-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] hover:bg-argan-deep transition-colors duration-500"
              >
                {t('welcomeBox.ctaPrimary')}
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
              </LocalizedLink>
              <LocalizedLink
                to="condizioniWelcomeBox"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-bruno/30 text-bruno text-[11px] uppercase tracking-[0.2em] hover:border-argan hover:text-argan-deep transition-colors duration-500"
              >
                {t('welcomeBox.ctaTerms')}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
